/**
 * Centralized API route definitions.
 *
 * Rules:
 * - Static routes are plain strings.
 * - Parameterized routes are arrow functions returning strings.
 * - ONLY api.js (service layer) should import from this file.
 * - Never import this directly inside components.
 */

// ── Auth ─────────────────────────────────────────────────────────────────────
export const AUTH = {
  LOGIN:           '/auth/teacher-login',
  LOGOUT:          '/auth/logout',
  REFRESH:         '/auth/refresh',
  CSRF_TOKEN:      '/auth/csrf-token',
  VERIFY_PASSWORD: '/auth/verify-password',
  SIGNAL_INACTIVE: '/auth/signal-inactive',
};

export const BILLING = Object.freeze({
  ENTITLEMENT: '/billing/entitlement',
  SUMMARY: '/billing/summary',
  TRIAL_END_NOTICE: '/billing/trial-end-notice',
  CHECKOUT: '/billing/checkout',
  CHECKOUT_STATUS: (sessionId) => `/billing/checkout-status?session_id=${encodeURIComponent(sessionId)}`,
  PORTAL: '/billing/portal',
  REFERRAL: '/billing/referral',
});

// ── Teacher ───────────────────────────────────────────────────────────────────
export const TEACHER = {
  INFO:             '/teacher/info',
  CONFIG:           '/config',
  MESSAGES:         '/messages',
  CLASS_LEVELS:     '/reference/class-levels',
};

// ── Families ──────────────────────────────────────────────────────────────────
export const FAMILIES = {
  LIST:             '/families',
  BY_ID:            (id) => `/families/${id}`,
};

// ── Students ──────────────────────────────────────────────────────────────────
export const STUDENTS = {
  LIST:             '/students',
  SUGGESTIONS:      '/students/suggestions',
  BY_ID:            (id) => `/students/${id}`,
  ARCHIVE:          (id) => `/students/${id}/archive`,
  UNARCHIVE:        (id) => `/students/${id}/unarchive`,
};

// ── Lessons ───────────────────────────────────────────────────────────────────
export const LESSONS = {
  LIST:             '/lessons',
  BY_ID:            (id) => `/lessons/${id}`,
  BY_STUDENT:       (studentId) => `/lessons/student/${studentId}`,
  DATE_RANGE:       (start, end) => `/lessons/date-range?start_date=${start}&end_date=${end}`,
};

// ── Schedules ─────────────────────────────────────────────────────────────────
export const SCHEDULES = {
  LIST:             '/lesson-schedules',
  BY_ID:            (id) => `/lesson-schedules/${id}`,
  BY_STUDENT:       (studentId) => `/lesson-schedules/student/${studentId}`,
};

// ── Invoices ──────────────────────────────────────────────────────────────────
export const INVOICES = {
  PREVIEW:          '/invoices/preview',
  SAVE:             '/invoices/save',
  HISTORY:          '/invoices/history',
  UNPAID_SUMMARY:   '/invoices/unpaid-summary',
  FAMILY_STATS:     (familyId) => `/invoices/family-preset-stats?family_id=${familyId}`,
  BULK_STATS:       '/invoices/bulk-preset-stats',
  EMAIL_TARGETS:    '/invoices/email-targets',
  GENERATE_PDFS:    '/invoices/generate-bulk-pdfs',
  CONFIRM_SEND:     '/invoices/confirm-bulk-send',
  SEND_BY_EMAIL:    '/invoices/send-pdf-by-email',
  GENERATE_PDF:     '/invoices/generate-pdf',
  DOWNLOAD:         (filename) => `/invoices/download/${filename}`,
};

// ── Payments ──────────────────────────────────────────────────────────────────
export const PAYMENTS = {
  LIST:             '/payments',
  CREATE:           '/payments',
  STATS:            '/payments/stats',
  METHODS:          '/payment-methods',
  PENDING:          '/payments/pending',
  FAMILY_INFO:      (familyId) => `/payments/family/${familyId}/info`,
};

// ── Statistics ────────────────────────────────────────────────────────────────
export const STATISTICS = {
  BASIC:            '/statistics',
  LIFETIME_HOURS:   '/statistics/lifetime-hours',
  ADVANCED:         '/statistics/advanced',
  PROMOS:           '/reports/promos',
};

// ── Tasks ─────────────────────────────────────────────────────────────────────
export const TASKS = {
  LIST:             '/tasks',
  BY_ID:            (id) => `/tasks/${id}`,
  BY_LESSON:        (lessonId) => `/tasks/lesson/${lessonId}`,
};

// ── Evaluations ───────────────────────────────────────────────────────────────
export const EVALUATIONS = {
  LIST:             '/evaluations',
  BY_ID:            (id) => `/evaluations/${id}`,
  BY_STUDENT:       (studentId) => `/evaluations/student/${studentId}`,
  STATS:            (studentId) => `/evaluations/stats/${studentId}`,
};

// ── Homework ──────────────────────────────────────────────────────────────────
export const HOMEWORK = {
  LIST:             '/homework',
  BY_ID:            (id) => `/homework/${id}`,
  BY_STUDENT:       (studentId) => `/homework/student/${studentId}`,
};

// ── Course Sessions ───────────────────────────────────────────────────────────
export const COURSE_SESSIONS = {
  ACTIVE:           '/course-sessions/active',
  CREATE:           '/course-sessions',
  COMPLETE:         (id) => `/course-sessions/${id}/complete`,
  CANCEL:           (id) => `/course-sessions/${id}/cancel`,
};

// ── Game ──────────────────────────────────────────────────────────────────────
export const GAME = {
  RANDOM_ACTIVITY:  '/game/random-activity',
  ACTIVITIES_IMPORT: '/game/activities/import',
  RESULT:           '/game/result',
  MASTERY:          (studentId) => `/game/mastery/${studentId}`,
};

// ── Placement test (G2, stage B6) ─────────────────────────────────────────────
// ⚠ PUBLIC_TEST is the only route here that needs no session. Its two segments
// come from the address bar and are checked against a server-side allow-list;
// they are encoded on the way out so a crafted class level cannot escape the
// path it belongs in.
export const PLACEMENT = {
  CHECK:            (studentId) => `/placement/check?student_id=${studentId}`,
  TEST:             (studentId, subject) =>
                      `/placement/test?student_id=${studentId}`
                      + (subject ? `&subject=${encodeURIComponent(subject)}` : ''),
  SCHOOL_TYPE:      '/placement/school-type',
  SUBMIT:           '/placement/submit',
  PUBLIC_TEST:      (subject, classLevel) =>
                      `/tests/${encodeURIComponent(subject)}/${encodeURIComponent(classLevel)}`,
  PUBLIC_DIAGNOSTIC: (subject, classLevel) =>
                      `/tests/${encodeURIComponent(subject)}/${encodeURIComponent(classLevel)}/diagnostic`,
};

// ── Curriculum tagging (G2) ───────────────────────────────────────────────────
export const CURRICULUM = {
  SUGGEST_TOPIC:    '/tasks/suggest-topic',
  CONFIRM_TOPIC:    (taskId) => `/tasks/${taskId}/topic`,
};

// ── Urssaf ────────────────────────────────────────────────────────────────────
export const URSSAF = {
  SYNC_PAYMENTS:    '/urssaf/sync-payment-statuses',
  PAYMENT_DEMANDS:  '/urssaf/payment-demands',
};

// ── Calcul drill (T-CALC1) — PUBLIC, no session ───────────────────────────────
export const CALCUL = {
  CATALOGUE:          '/calcul/catalogue',
  SEARCH:             (query, freeText = false) => `/calcul/search?q=${encodeURIComponent(query)}${freeText ? '&free_text=1' : ''}`,
  SESSION:            '/calcul/session',
  SKILL_SCHEMA:       '/calcul/skill-schema',
  SKILL_SESSION:      '/calcul/skills/session',
  QR:                 (path) => `/calcul/qr?path=${encodeURIComponent(path)}`,
  NUMBER_WORDS_SESSION: (params) => `/calcul/nombres-en-lettres/session?${new URLSearchParams(params)}`,
  NUMBER_WORDS_CHECK: '/calcul/nombres-en-lettres/check',
};

export const NOTION_PRACTICE = {
  CATALOGUE: (classLevel) => `/game/notion-practice?class_level=${encodeURIComponent(classLevel)}`,
  SESSION: '/game/notion-practice',
};

// ── Mission drafter (teacher-only) ──────────────────────────────────────────
export const MISSION_DRAFTS = {
  DRAFT: '/missions/draft',
};

// ── National exams (PUBLIC, stateless) ──────────────────────────────────────
export const NATIONAL_EXAMS = {
  PRACTICE_GRADE: '/exam/practice/grade',
  CATALOGUE: '/examens',
  SUBJECT:   (exam, year, session) =>
               `/examens/${encodeURIComponent(exam)}/${encodeURIComponent(year)}/${encodeURIComponent(session)}`,
  GRADE:     (exam, year, session) =>
               `${NATIONAL_EXAMS.SUBJECT(exam, year, session)}/grade`,
  PDF:       (exam, year, session) =>
               `${NATIONAL_EXAMS.SUBJECT(exam, year, session)}/pdf`,
  // Examination Chamber: anonymous persisted sessions (bearer per session).
  SUBJECTS:      '/exam/subjects',
  SESSION:       '/exam/session',
  SESSION_BY_ID: (sessionId) => `/exam/session/${encodeURIComponent(sessionId)}`,
  RESULT:        '/exam/result',
  RESULT_BY_ID:  (resultId) => `/exam/result/${encodeURIComponent(resultId)}`,
};

// ── Backup ────────────────────────────────────────────────────────────────────
export const BACKUP = {
  CREATE:           '/backup',
};

// ── Formations / Courses (FORM1) — own session cookie, never the teacher session ──
export const FORMATIONS = {
  OFFERS:          '/formations/offers',
  ME:              '/formations/me',
  AUTH_LINK:       '/formations/auth/link',
  AUTH_REDEEM:     '/formations/auth/redeem',
  AUTH_COMPLETE:   '/formations/auth/complete',
  AUTH_LOGOUT:     '/formations/auth/logout',
  DEVICES:         '/formations/devices',
  DEVICE_REVOKE:   (sessionId) => `/formations/devices/${encodeURIComponent(sessionId)}/revoke`,
  ACADEMIC_CODE:   '/formations/academic/code',
  ACADEMIC_VERIFY: '/formations/academic/verify',
  CHECKOUT:        '/formations/checkout',
  CHECKOUT_STATUS: '/formations/checkout/status',
  ENROLL:          '/formations/enroll',
  LESSON:          (courseKey, lessonId) =>
                     `/formations/lessons/${encodeURIComponent(courseKey)}/${encodeURIComponent(lessonId)}`,
};

// LAND3 — landing simulator e-mail leads (public).
export const MARKETING_LEADS = Object.freeze({
  SUBMIT: '/public/leads',
});
